import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import apiClient from '../../core/apiClient';
import './AuthPages.css';

interface IndividualSignupForm {
    full_name: string;
    dob: string;
    email: string;
    phone_number: string;
    country_code: string;
    id_type: string;
    password: string;
    confirm_password: string;
}

export const IndividualSignupPage: React.FC = () => {
    const navigate = useNavigate();
    const [formData, setFormData] = useState<IndividualSignupForm>({
        full_name: '',
        dob: '',
        email: '',
        phone_number: '',
        country_code: '+91',
        id_type: 'Aadhaar',
        password: '',
        confirm_password: ''
    });

    const [verificationState, setVerificationState] = useState({
        emailSent: false,
        emailVerified: false,
        emailOtp: '',
        phoneSent: false,
        phoneVerified: false,
        phoneOtp: '',
        idUploaded: false,
        idFileUrl: '',
        idFileName: '',
        termsAccepted: false
    });

    const [loading, setLoading] = useState({
        email: false,
        phone: false,
        id: false,
        submit: false
    });

    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');

    const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
        setError('');
    };

    const handleEmailAction = async () => {
        if (!formData.email) {
            setError('Please enter your email address first');
            return;
        }
        
        // Simple regex for email validation
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(formData.email)) {
            setError('Please enter a valid email address');
            return;
        }

        if (verificationState.emailVerified) return;

        setLoading(prev => ({ ...prev, email: true }));
        setError('');
        try {
            if (!verificationState.emailSent) {
                // In this architecture, initiate-signup sends the email OTP
                const response = await apiClient.post('/api/auth/initiate-signup', {
                    email: formData.email,
                    phone_number: formData.phone_number || '0000000000',
                    user_type: 'individual'
                });
                setVerificationState(prev => ({ ...prev, emailSent: true }));
                setSuccess('OTP sent to your email.');
            } else {
                await apiClient.post('/api/auth/verify-email', {
                    email: formData.email,
                    code: verificationState.emailOtp
                });
                setVerificationState(prev => ({ ...prev, emailVerified: true }));
                setSuccess('Email verified successfully');
            }
        } catch (err: any) {
            setError(err.response?.data?.message || 'Email action failed');
        } finally {
            setLoading(prev => ({ ...prev, email: false }));
        }
    };

    const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (!file) return;

        setLoading(prev => ({ ...prev, id: true }));
        setError('');
        const uploadData = new FormData();
        uploadData.append('file', file);

        try {
            const res = await apiClient.post('/api/upload', uploadData, {
                headers: { 'Content-Type': 'multipart/form-data' }
            });
            const fileUrl = res.data?.data?.file_url || res.data?.file_url;
            setVerificationState(prev => ({
                ...prev,
                idUploaded: true,
                idFileUrl: fileUrl,
                idFileName: file.name
            }));
            setSuccess('ID proof uploaded successfully');
        } catch (err: any) {
            setError(err.response?.data?.message || 'Upload failed');
        } finally {
            setLoading(prev => ({ ...prev, id: false }));
        }
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!verificationState.emailVerified || !verificationState.idUploaded) {
            setError('Please complete all verifications before creating account');
            return;
        }
        if (formData.password !== formData.confirm_password) {
            setError('Passwords do not match');
            return;
        }
        if (!verificationState.termsAccepted) {
            setError('Please accept the terms and conditions');
            return;
        }

        setLoading(prev => ({ ...prev, submit: true }));
        setError('');

        try {
            await apiClient.post('/api/auth/signup', {
                ...formData,
                user_type: 'individual',
                id_proof_file_url: verificationState.idFileUrl,
                id_proof_type: formData.id_type,
                email_otp: verificationState.emailOtp
            });
            navigate('/login');
        } catch (err: any) {
            setError(err.response?.data?.message || 'Signup failed');
        } finally {
            setLoading(prev => ({ ...prev, submit: false }));
        }
    };

    return (
        <div className="auth-container">
            <div className="auth-card signup-card">
                <div className="auth-card-content">
                    <div className="auth-header">
                        <h1>Individual Signup</h1>
                        <p>Join ExiBrain today and experience next-gen AI orchestration.</p>
                        <div className="note-box">
                            <p><i className="fas fa-info-circle"></i> Verified accounts enjoy priority processing and enhanced security features.</p>
                        </div>
                    </div>

                {error && <div className="error-message"><i className="fas fa-exclamation-circle"></i> {error}</div>}
                {success && <div className="success-message"><i className="fas fa-check-circle"></i> {success}</div>}

                <form onSubmit={handleSubmit} className="auth-form">
                    {/* Personal Information */}
                    <div className="form-section">
                        <div className="form-section-header">
                            <i className="fas fa-user"></i>
                            <h3>Personal Information</h3>
                        </div>
                        <div className="form-row">
                            <div className="form-group">
                                <label>Full Name</label>
                                <input
                                    type="text"
                                    name="full_name"
                                    placeholder="John Doe"
                                    value={formData.full_name}
                                    onChange={handleChange}
                                    required
                                />
                            </div>
                            <div className="form-group">
                                <label>Date of Birth</label>
                                <input
                                    type="date"
                                    name="dob"
                                    value={formData.dob}
                                    onChange={handleChange}
                                    required
                                />
                            </div>
                        </div>
                    </div>

                    {/* Contact Information */}
                    <div className="form-section">
                        <div className="form-section-header">
                            <i className="fas fa-envelope"></i>
                            <h3>Contact Information</h3>
                        </div>
                        
                        <div className="inline-verification">
                            <div className="form-group">
                                <label>Email Address</label>
                                <input
                                    type="email"
                                    name="email"
                                    placeholder="john@example.com"
                                    value={formData.email}
                                    onChange={handleChange}
                                    disabled={verificationState.emailVerified}
                                    required
                                />
                            </div>
                            <button
                                type="button"
                                className={`btn ${verificationState.emailVerified ? 'btn-success' : 'btn-secondary'}`}
                                onClick={handleEmailAction}
                                disabled={loading.email || !formData.email || (verificationState.emailSent && !verificationState.emailOtp && !verificationState.emailVerified)}
                            >
                                {loading.email ? '...' : (verificationState.emailVerified ? 'Verified ✓' : (verificationState.emailSent ? 'Verify' : 'Send OTP'))}
                            </button>
                        </div>
                        {verificationState.emailSent && !verificationState.emailVerified && (
                            <div className="verification-control">
                                <label>Enter Email OTP</label>
                                <div className="otp-input-wrapper">
                                    <input
                                        type="text"
                                        maxLength={6}
                                        placeholder="000000"
                                        value={verificationState.emailOtp}
                                        onChange={(e) => setVerificationState(prev => ({ ...prev, emailOtp: e.target.value }))}
                                    />
                                </div>
                            </div>
                        )}

                        <div className="form-row">
                            <div className="form-group" style={{ flex: '0 0 100px', minWidth: '80px' }}>
                                <label>Code</label>
                                <select name="country_code" value={formData.country_code} onChange={handleChange}>
                                    <option value="+91">+91</option>
                                    <option value="+1">+1</option>
                                    <option value="+44">+44</option>
                                </select>
                            </div>
                            <div className="form-group">
                                <label>Phone Number</label>
                                <input
                                    type="tel"
                                    name="phone_number"
                                    placeholder="9876543210"
                                    value={formData.phone_number}
                                    onChange={handleChange}
                                    required
                                />
                            </div>
                        </div>
                    </div>

                    {/* ID Proof Verification */}
                    <div className="form-section">
                        <div className="form-section-header">
                            <i className="fas fa-id-card"></i>
                            <h3>ID Proof Verification</h3>
                        </div>
                        <div className="form-group" style={{ marginBottom: '1rem' }}>
                            <label>ID Type</label>
                            <select name="id_type" value={formData.id_type} onChange={handleChange}>
                                <option value="Aadhaar">Aadhaar</option>
                                <option value="PAN">PAN</option>
                                <option value="Passport">Passport</option>
                                <option value="Driving Licence">Driving Licence</option>
                                <option value="Voter ID">Voter ID</option>
                                <option value="Other">Other</option>
                            </select>
                        </div>
                        
                        <div className="upload-area" onClick={() => document.getElementById('id-upload')?.click()}>
                            <i className="fas fa-cloud-upload-alt"></i>
                            <p><strong>Click to upload</strong> or drag and drop</p>
                            <p className="form-hint">JPG, PNG, PDF (Max 5MB)</p>
                            <input
                                id="id-upload"
                                type="file"
                                hidden
                                accept=".jpg,.jpeg,.png,.pdf"
                                onChange={handleFileUpload}
                            />
                        </div>
                        {verificationState.idUploaded && (
                            <div className="file-preview">
                                <span><i className="fas fa-file-alt"></i> {verificationState.idFileName}</span>
                                <span className="status-badge verified">Ready</span>
                            </div>
                        )}
                        <p className="form-hint" style={{ marginTop: '0.5rem' }}>Upload any government-issued ID proof</p>
                    </div>

                    {/* Security */}
                    <div className="form-section">
                        <div className="form-section-header">
                            <i className="fas fa-lock"></i>
                            <h3>Security</h3>
                        </div>
                        <div className="form-row">
                            <div className="form-group">
                                <label>Password</label>
                                <input
                                    type="password"
                                    name="password"
                                    placeholder="••••••••"
                                    value={formData.password}
                                    onChange={handleChange}
                                    required
                                />
                            </div>
                            <div className="form-group">
                                <label>Confirm Password</label>
                                <input
                                    type="password"
                                    name="confirm_password"
                                    placeholder="••••••••"
                                    value={formData.confirm_password}
                                    onChange={handleChange}
                                    required
                                />
                            </div>
                        </div>
                        <div className="form-group" style={{ flexDirection: 'row', alignItems: 'center', gap: '0.5rem', marginTop: '1rem' }}>
                            <input
                                type="checkbox"
                                id="terms"
                                checked={verificationState.termsAccepted}
                                onChange={(e) => setVerificationState(prev => ({ ...prev, termsAccepted: e.target.checked }))}
                                style={{ width: 'auto' }}
                            />
                            <label htmlFor="terms" style={{ fontWeight: 'normal' }}>
                                I agree to the <Link to="/terms" style={{ color: '#0284c7' }}>Terms and Conditions</Link>
                            </label>
                        </div>
                    </div>

                    <button
                        type="submit"
                        className="btn btn-primary btn-block signup-submit-btn"
                        disabled={loading.submit}
                    >
                        {loading.submit ? 'Creating Account...' : 'Create Account'}
                    </button>

                    <p style={{ textAlign: 'center', marginTop: '1.5rem', color: '#64748b' }}>
                        Already have an account? <Link to="/login" style={{ color: '#0284c7', fontWeight: '600' }}>Sign In</Link>
                    </p>
                </form>
                </div>
            </div>
        </div>
    );
};
